import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import UserProfile from '../components/UserProfile';
import CoupleMatching from "../components/CoupleMatching";
import CoupleInfo from '../components/CoupleInfo';
import PendingInvite from '../components/PendingInvite';
import LeaveCoupleConfirm from '../components/LeaveCoupleConfirm';
import UsageGuide from '../components/UsageGuide';
import '../styles/Home.css';
import { useToast } from '../components/Toast';
import { apiFetch } from '../utils/apiFetch';

const Home = () => {
    const toast = useToast();
    const [user, setUser] = useState(null);
    const [coupleInfo, setCoupleInfo] = useState(null);
    const [pendingInvite, setPendingInvite] = useState(null);   // { inviteCode, expiresAt } — 매칭 대기 상태
    const [isLoading, setIsLoading] = useState(true);
    const [isCoupleInfoLoading, setIsCoupleInfoLoading] = useState(false);
    const [isCoupleMatchingOpen, setIsCoupleMatchingOpen] = useState(false);
    const [isLeaveCoupleOpen, setIsLeaveCoupleOpen] = useState(false);
    const [isLeaveLoading, setIsLeaveLoading] = useState(false);
    const [isUsageGuideOpen, setIsUsageGuideOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
            navigate('/login');
            return;
        }
        
        try {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            
            // 로그인 시 저장된 커플/대기 정보 확인
            const savedCoupleInfo = localStorage.getItem('coupleInfo');
            const savedPendingInvite = localStorage.getItem('pendingInvite');
            if (savedCoupleInfo) {
                // 캐시를 먼저 보여주고, 서버 재검증으로 최신 상태를 확인
                setCoupleInfo(JSON.parse(savedCoupleInfo));
            }
            if (savedPendingInvite) {
                setPendingInvite(JSON.parse(savedPendingInvite));
            }
            // 커플이거나 대기 중일 가능성이 있을 때만 서버 재검증 — 싱글이면 요청을 생략해 404 로그를 남기지 않음
            if (savedCoupleInfo || savedPendingInvite || parsedUser.coupleId) {
                fetchCoupleInfo();
            }
            
        } catch (error) {
            console.error('사용자 정보 파싱 오류:', error);
            navigate('/login');
            return;
        }
        
        setIsLoading(false);
        // 마운트 시 1회 재검증 — fetchCoupleInfo는 상태 세터·localStorage만 쓰므로 의존성에서 의도적으로 제외
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    // 매칭 대기 상태 저장/정리 — localStorage 힌트가 있어야 다음 접속 때 재검증 요청을 보냄
    const savePendingInvite = (invite) => {
        setPendingInvite(invite);
        localStorage.setItem('pendingInvite', JSON.stringify(invite));
    };

    const clearPendingInvite = () => {
        setPendingInvite(null);
        localStorage.removeItem('pendingInvite');
    };

    // 커플 관련 상태·캐시를 싱글 상태로 정리
    const clearCoupleState = () => {
        localStorage.removeItem('coupleInfo');
        setCoupleInfo(null);
        setUser(prev => prev ? { ...prev, coupleId: null } : prev);
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            try {
                const parsed = JSON.parse(savedUser);
                parsed.coupleId = null;
                localStorage.setItem('user', JSON.stringify(parsed));
            } catch (e) {
                console.error('user 캐시 갱신 실패:', e);
            }
        }
    };

    const fetchCoupleInfo = async () => {
        setIsCoupleInfoLoading(true);
        try {
            const response = await apiFetch('/members/couple/info', {
                method: 'GET'
            });

            if (response.ok) {
                const result = await response.json();
                if (!result.data.matched) {
                    // 매칭 대기(1인) 커플은 커플로 표시하지 않고 대기 카드로 안내 — 잘못 저장된 캐시도 여기서 정리됨
                    clearCoupleState();
                    savePendingInvite({
                        inviteCode: result.data.inviteCode,
                        expiresAt: result.data.inviteExpiresAt
                    });
                } else {
                    clearPendingInvite();
                    setCoupleInfo(result.data);
                    localStorage.setItem('coupleInfo', JSON.stringify(result.data));
                }
            } else {
                const errorData = await response.json().catch(() => null);
                if (errorData && errorData.errorCode === 'C006') {
                    // 실제로 커플이 없는 경우에만 캐시 제거 + user.coupleId 정리
                    clearCoupleState();
                    clearPendingInvite();
                } else {
                    // 일시적 오류 — 캐시 유지
                    console.error('커플 정보 조회 실패:', response.status, errorData);
                }
            }
        } catch (error) {
            // 네트워크 오류 — 캐시 유지
            console.error('커플 정보 조회 오류:', error);
        } finally {
            setIsCoupleInfoLoading(false);
        }
    };

    const handleCoupleMatchingClick = () => {
        setIsCoupleMatchingOpen(true);
    };

    const handleCloseCoupleMatching = () => {
        setIsCoupleMatchingOpen(false);
    };

    // 커플 매칭 성공 시 호출되는 함수
    const handleCoupleMatchingSuccess = (newCoupleInfo) => {
        // 로컬 스토리지 업데이트
        const updatedUser = {
            ...user,
            coupleId: newCoupleInfo.coupleId
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // 커플 정보 상태 및 로컬 스토리지 업데이트
        clearPendingInvite();
        setCoupleInfo(newCoupleInfo);
        localStorage.setItem('coupleInfo', JSON.stringify(newCoupleInfo));

        // 모달 닫기
        setIsCoupleMatchingOpen(false);
    };

    // 초대 코드 발급·발견 시 대기 카드 표시
    const handleInviteIssued = (inviteInfo) => {
        savePendingInvite({
            inviteCode: inviteInfo.inviteCode,
            expiresAt: inviteInfo.expiresAt
        });
    };

    // 매칭 대기 상태에서 초대 취소 (대기 커플 정리)
    const handleCancelInvite = async () => {
        if (!window.confirm('초대를 취소하시겠어요? 발급된 초대 코드는 사용할 수 없게 됩니다.')) {
            return;
        }

        try {
            const response = await apiFetch('/members/couple', {
                method: 'DELETE'
            });

            const errorData = response.ok ? null : await response.json().catch(() => null);
            if (response.ok || (errorData && errorData.errorCode === 'C006')) {
                // 이미 정리된 경우(C006)도 성공과 동일하게 처리
                clearPendingInvite();
                toast.success('초대가 취소되었습니다.');
            } else {
                toast.error((errorData && errorData.errorMessage) || '초대 취소에 실패했습니다.');
            }
        } catch (error) {
            console.error('초대 취소 오류:', error);
            toast.error('초대 취소에 실패했습니다.');
        }
    };

    // 커플 정보 업데이트 (시작일 수정 등)
    const handleCoupleInfoUpdate = (updatedCoupleInfo) => {
        setCoupleInfo(updatedCoupleInfo);
        localStorage.setItem('coupleInfo', JSON.stringify(updatedCoupleInfo));
    };

    // 커플 탈퇴 — 되돌릴 수 없으므로 전용 확인 모달을 거친다
    const handleLeaveCouple = () => {
        setIsLeaveCoupleOpen(true);
    };

    const handleLeaveCoupleConfirm = async () => {
        setIsLeaveLoading(true);

        try {
            const response = await apiFetch('/members/couple', {
                method: 'DELETE'
            });

            if (response.ok) {
                // 커플 관련 상태·캐시 정리 → 매칭 전 화면으로 복귀
                clearCoupleState();
                setIsLeaveCoupleOpen(false);
                toast.success('커플 탈퇴가 완료되었습니다.');
            } else {
                const errorData = await response.json().catch(() => null);
                if (errorData && errorData.errorCode === 'C006') {
                    // 이미 종료된 커플 — 성공과 동일하게 정리
                    clearCoupleState();
                    setIsLeaveCoupleOpen(false);
                    toast.success('커플 탈퇴가 완료되었습니다.');
                } else {
                    toast.error((errorData && errorData.errorMessage) || '커플 탈퇴에 실패했습니다.');
                }
            }
        } catch (error) {
            console.error('커플 탈퇴 오류:', error);
            toast.error('커플 탈퇴에 실패했습니다.');
        } finally {
            setIsLeaveLoading(false);
        }
    };

    const handleDiaryWriteClick = () => {
        // 커플 관계인지 확인
        if (!user.coupleId) {
            toast.error('커플 매칭 후 일기를 작성할 수 있습니다.');
            return;
        }
        navigate('/diary/new');
    };

    const handleDiaryCalendarClick = () => {
        if (!user.coupleId) {
            toast.error('커플 매칭 후 일기를 확인할 수 있습니다.');
            return;
        }
        navigate('/diary');
    };

    if (isLoading) {
        return (
            <div className="home-loading">
                <div className="loading-spinner"></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    // 사용 방법 안내 카드 — 싱글/커플에 따라 그리드 내 위치가 달라 변수로 분리
    const usageGuideCard = (
        <div
            className="action-card clickable"
            onClick={() => setIsUsageGuideOpen(true)}
            style={{ cursor: 'pointer' }}
        >
            <div className="action-icon">❓</div>
            <h3>사용 방법</h3>
            <p>ReUs를 200% 활용하는 방법을 알아보세요</p>
        </div>
    );

    return (
        <div className="home-container">
            <AppHeader />

            <main className="home-main">
                <div className="home-content">
                    <UserProfile user={user} />
                    
                    <section className="main-actions">
                        <h2>무엇을 하고 싶으신가요?</h2>
                        
                        {/* 사용 방법 카드는 싱글이면 맨 앞, 커플이면 맨 뒤에 배치 */}
                        <div className="action-grid">
                            {!user.coupleId && usageGuideCard}

                            {/* 커플 매칭 버튼 (싱글일 때만 표시) */}
                            {!user.coupleId && (
                                <div
                                    className="action-card clickable"
                                    onClick={handleCoupleMatchingClick}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="action-icon">💕</div>
                                    <h3>커플 매칭</h3>
                                    <p>커플 ID를 생성하거나 입력해서 매칭하세요</p>
                                </div>
                            )}

                            {/* 일기 쓰기 */}
                            <div
                                className="action-card clickable"
                                onClick={handleDiaryWriteClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="action-icon">📝</div>
                                <h3>일기 쓰기</h3>
                                <p>오늘의 추억을 기록해보세요</p>
                            </div>

                            {/* 일기 보기 카드 추가 */}
                            <div
                                className="action-card clickable"
                                onClick={handleDiaryCalendarClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="action-icon">📖</div>
                                <h3>일기 보기</h3>
                                <p>작성한 일기를 달력에서 확인하세요</p>
                            </div>

                            {/* 프로필 수정 버튼 */}
                            <div
                                className="action-card clickable"
                                onClick={() => navigate('/profile')}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="action-icon">⚙️</div>
                                <h3>프로필 수정</h3>
                                <p>닉네임과 알림 이메일을 수정하세요</p>
                            </div>

                            {user.coupleId && usageGuideCard}
                        </div>
                    </section>

                    {/* 커플 정보 로딩 중 */}
                    {isCoupleInfoLoading && (
                        <div className="couple-info-loading">
                            <div className="loading-spinner"></div>
                            <p>커플 정보를 불러오는 중...</p>
                        </div>
                    )}
                    
                    {/* 커플 정보 표시 */}
                    {coupleInfo && !isCoupleInfoLoading && (
                        <CoupleInfo
                            coupleInfo={coupleInfo}
                            currentUser={user}
                            onCoupleInfoUpdate={handleCoupleInfoUpdate}
                            onLeaveCouple={handleLeaveCouple}
                        />
                    )}

                    {/* 매칭 대기 중 (초대 코드 발급 상태) */}
                    {pendingInvite && !coupleInfo && !isCoupleInfoLoading && (
                        <PendingInvite
                            invite={pendingInvite}
                            onReissue={() => setIsCoupleMatchingOpen(true)}
                            onCancel={handleCancelInvite}
                        />
                    )}

                    {/* 싱글 상태일 때 안내 메시지 */}
                    {!user.coupleId && !isCoupleInfoLoading && !coupleInfo && !pendingInvite && (
                        <section className="single-info-section">
                            <div className="single-info-card">
                                <h3>💝 커플 매칭 안내</h3>
                                <p>위의 <strong>커플 매칭</strong> 버튼을 클릭하여:</p>
                                <ul>
                                    <li>새로운 커플 ID를 생성하거나</li>
                                    <li>상대방의 커플 ID를 입력하여 매칭하세요</li>
                                </ul>
                            </div>
                        </section>
                    )}
                </div>
            </main>

            {/* 커플 매칭 모달 */}
            {isCoupleMatchingOpen && (
                <CoupleMatching
                    onClose={handleCloseCoupleMatching}
                    onSuccess={handleCoupleMatchingSuccess}
                    onInviteIssued={handleInviteIssued}
                />
            )}

            {/* 사용 방법 안내 모달 */}
            {isUsageGuideOpen && (
                <UsageGuide onClose={() => setIsUsageGuideOpen(false)} />
            )}

            {/* 커플 탈퇴 확인 모달 */}
            {isLeaveCoupleOpen && (
                <LeaveCoupleConfirm
                    onClose={() => setIsLeaveCoupleOpen(false)}
                    onConfirm={handleLeaveCoupleConfirm}
                    isLoading={isLeaveLoading}
                />
            )}
        </div>
    );
};

export default Home;
